import { queryOptions, experimental_streamedQuery as streamedQuery, useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { useFormik } from 'formik';
import { useEffect, useTransition } from 'react';
import { functions } from '../../firebase';
import { useChat } from '../../hooks/useChat';
import { IMessage } from '../../screens/Prompt/Prompt';
import { Message } from './Message';
import { TypingIndicator } from './TypingIndicator';
import React from 'react';

interface ChatStructure {
  messages: {
    role: 'user' | 'assistant';
    id: string;
    parts: {
      type: 'text';
      text: string;
    }[];
  }[];
  systemPrompt: string;
  temperature: number;
  status: string;
}

const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUUIDV4 = (value?: string) => {
  if (!value) {
    return false;
  }
  return uuidV4Regex.test(value);
};
const sendMessage = httpsCallable(functions, 'prompt');

const chatOptions = (structure: ChatStructure | null) => {
  return queryOptions({
    queryKey: ['chat-prompt', structure?.status, structure?.systemPrompt, structure?.temperature],
    queryFn: streamedQuery({
      queryFn: async () => {
        const { stream } = await sendMessage.stream({
          messages: structure?.messages,
          systemPrompt: structure?.systemPrompt,
          temperature: structure?.temperature,
        });
        return stream;
      },
    }),
    enabled: isUUIDV4(structure?.status),
    staleTime: Infinity,
  });
};

interface Props {
  messages: IMessage[];
  systemPrompt: string;
  temperature: number;
  status: string;
  setNotification: ReturnType<typeof useChat>['setNotification'];
  setMessages: ReturnType<typeof useChat>['setMessages'];
  setStatus: ReturnType<typeof useFormik>['setStatus'];
}

export const StreamedMessage = ({
  messages,
  systemPrompt,
  temperature,
  status,
  setNotification,
  setMessages,
  setStatus,
}: Props) => {
  const [_, startTransition] = useTransition();
  const {
    data: chatData,
    isLoading,
    fetchStatus,
    isError,
  } = useQuery(
    chatOptions({
      messages: messages.map((m) => ({
        role: m.sender,
        id: m.id,
        parts: [{ type: 'text' as const, text: m.text }],
      })),
      systemPrompt,
      temperature,
      status,
    }),
  );

  useEffect(() => {
    if (fetchStatus === 'idle' && isError) {
      setNotification({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    }
  }, [fetchStatus, isError]);

  useEffect(() => {
    if (fetchStatus === 'idle' && chatData?.length) {
      startTransition(() => {
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), text: chatData?.join(''), sender: 'assistant' } satisfies IMessage,
        ]);
        setStatus('idle');
      });
    }
  }, [fetchStatus]);

  if (!isUUIDV4(status)) {
    return null;
  }
  if (isLoading) {
    return <TypingIndicator />;
  }
  return (
    <Message isStreamed message={{ id: crypto.randomUUID(), text: chatData?.join('') + '...', sender: 'assistant' }} />
  );
};
